import { Link } from "react-router-dom";
import { ScanLine, ShieldCheck, BellRing, Eye, Lock, Car, ArrowRight } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1771210363520-7ee7d4106d5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXIlMjBwYXJrZWQlMjBjaXR5JTIwc3RyZWV0fGVufDB8fHx8MTc4MTM0NDkwMnww&ixlib=rb-4.1.0&q=85";
const phoneImg = "https://images.unsplash.com/photo-1592890288564-76628a30a657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjB1cmJhbnxlbnwwfHx8fDE3ODEzNDQ5MDJ8MA&ixlib=rb-4.1.0&q=85";
const windshieldImg = "https://images.unsplash.com/photo-1602970890693-e576d76a900f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxjYXIlMjB3aW5kc2hpZWxkJTIwY2xvc2V1cHxlbnwwfHx8fDE3ODEzNDQ5MDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Top bar */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="logo">
            <div className="w-7 h-7 bg-[#002FA7] grid place-items-center text-white font-display font-extrabold text-sm">Q</div>
            <span className="font-display font-extrabold tracking-tight text-lg">QAAB</span>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/login" className="text-sm font-medium hover:text-[#002FA7]" data-testid="nav-login">Sign in</Link>
            <Link to="/login?mode=register" className="btn btn-primary text-sm" data-testid="nav-register">Get your card</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">Vehicle contact · Identity-verified</div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                A stranger needs<br />
                to reach you.<br />
                <span className="text-[#002FA7]">Your number stays private.</span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 max-w-2xl mb-10 leading-relaxed">
                Qaab is a windscreen QR card that lets anyone alert you about a blocked car, emergency, lights left on — without seeing your phone number. Verified scanners only. AI-checked reports.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/login?mode=register" className="btn btn-primary" data-testid="hero-cta-register">
                  Activate a card <ArrowRight size={16} />
                </Link>
                <a href="#how" className="btn btn-ghost" data-testid="hero-cta-learn">How it works</a>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                <div>
                  <div className="font-display font-black text-3xl tabular">87%</div>
                  <div className="label-eyebrow mt-1">AI verified</div>
                </div>
                <div>
                  <div className="font-display font-black text-3xl tabular">0</div>
                  <div className="label-eyebrow mt-1">Number leaked</div>
                </div>
                <div>
                  <div className="font-display font-black text-3xl tabular">90s</div>
                  <div className="label-eyebrow mt-1">Median reach</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] overflow-hidden border border-gray-200">
                <img src={heroImg} alt="Modern car parked on street" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="qr-card text-black">
                    <div className="flex items-start justify-between mb-3 mt-1">
                      <div>
                        <div className="label-eyebrow">If you need to reach me</div>
                        <div className="font-display font-bold text-xl mt-1">Scan this card</div>
                      </div>
                      <div className="w-16 h-16 bg-black grid place-items-center">
                        <ScanLine className="text-white" size={32} />
                      </div>
                    </div>
                    <div className="font-mono text-xs text-gray-500 mt-2">QAB-48271 · qaab.id</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-gray-200 bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="label-eyebrow mb-3">How it works</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mb-12 max-w-3xl">
            Three steps. Then your car protects itself.
          </h2>

          <div className="grid lg:grid-cols-3 gap-6">
            <Step n="01" title="Stick the card" img={windshieldImg} desc="Mount the printed QR card behind your windscreen. Pair it to your plate in 30 seconds." icon={<Car size={20} />} />
            <Step n="02" title="Stranger scans" img={phoneImg} desc="Anyone with a phone can scan to alert you — blocked parking, emergency, lights on. Identity required for serious reports." icon={<ScanLine size={20} />} />
            <Step n="03" title="You decide" img={heroImg} desc="Verified alert lands on your phone with AI verdict and scanner identity. Confirm, dismiss, or report false." icon={<BellRing size={20} />} />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#111827] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="label-eyebrow text-gray-400 mb-4">Built on trust</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Abusers get banned.<br />Honest scanners get faster.<br />Owners stay anonymous.
              </h2>
            </div>
            <div className="space-y-6">
              <Feature icon={<Lock size={20} />} title="Number never shared" desc="Your phone number stays in our vault. Scanners reach you only through the platform." />
              <Feature icon={<ShieldCheck size={20} />} title="Identity for serious reports" desc="Blocked parking & emergencies require name, OTP-verified phone, plate, live selfie + car photo." />
              <Feature icon={<Eye size={20} />} title="AI photo verification" desc="Every scene photo is checked by Claude vision. Recycled or staged photos are flagged." />
              <Feature icon={<BellRing size={20} />} title="Graduated blacklist" desc="False reporters get warnings → suspensions → permanent bans. Device fingerprint persists across VPN." />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-gray-200 py-20 bg-[#F9F9F8]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="label-eyebrow mb-3">Get started</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mb-6">
            One card. Lifetime peace of mind.
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Activate your QR card, pair your plate, and your car becomes reachable to the world — without exposing you.
          </p>
          <Link to="/login?mode=register" className="btn btn-primary" data-testid="footer-cta">
            Activate my card <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-gray-200 py-8 text-center text-sm text-gray-500 bg-white">
        <div className="font-display font-bold tracking-widest">QAAB · vehicle contact platform</div>
        <div className="mt-1 font-mono text-xs">v1.0 · global edition</div>
      </footer>
    </div>
  );
}

function Step({ n, title, desc, img, icon }) {
  return (
    <div className="card-flat card-hover overflow-hidden" data-testid={`step-${n}`}>
      <div className="aspect-[16/10] overflow-hidden border-b border-gray-200">
        <img src={img} alt={title} className="w-full h-full object-cover" />
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-xs text-gray-500">{n}</div>
          <div className="w-9 h-9 bg-[#F9F9F8] border border-gray-200 grid place-items-center">{icon}</div>
        </div>
        <h3 className="font-display font-bold text-2xl mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="flex gap-4 border-t border-gray-700 pt-5">
      <div className="w-10 h-10 bg-white/10 grid place-items-center shrink-0">{icon}</div>
      <div>
        <div className="font-display font-bold text-lg">{title}</div>
        <div className="text-sm text-gray-400 mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
